import type { TFunction } from "i18next";
import { Api } from "services/api/api.service";
import { toApiPhoneNumber } from "utils/phoneNumber.logic";
import { resolveAddStaffErrorMessage } from "./addStaffPanel.error.logic";
import { mapAddStaffFormDataToWorkerPayload } from "./addStaffPanel.mapper";
import type { StampClockRegistrationFormData } from "./stampClockRegistration.contract";
import { toStampClockRegistrationAddStaffFormData } from "./stampClockRegistration.mapper";

export type StampClockRegistrationResult =
  | { readonly succeeded: true }
  | { readonly message: string; readonly succeeded: false };

const fetchEmployeeNumber = async (companyId: number): Promise<string> => {
  try {
    const response =
      await Api().company.organizationalStructure.getEmployeeNumber(companyId);
    return String(response.data ?? "");
  } catch {
    return "";
  }
};

export async function registerStampClockWorkerRequest({
  companyId,
  formData,
  teamId,
  translate,
}: {
  readonly companyId: number;
  readonly formData: StampClockRegistrationFormData;
  readonly teamId: string;
  readonly translate: TFunction;
}): Promise<StampClockRegistrationResult> {
  const apiPhoneNumber = toApiPhoneNumber(formData.phoneNumber);
  if (!apiPhoneNumber.succeeded) {
    return {
      succeeded: false,
      message: translate("Staff.AddStaffPanelPhoneNumberInvalid"),
    };
  }

  const addStaffFormData = toStampClockRegistrationAddStaffFormData({
    employeeNumber: await fetchEmployeeNumber(companyId),
    formData,
    teamId,
  });

  try {
    await Api().company.staff.createWorker(
      companyId,
      mapAddStaffFormDataToWorkerPayload(addStaffFormData, apiPhoneNumber.data),
    );
    return { succeeded: true };
  } catch (error) {
    return {
      succeeded: false,
      message: resolveAddStaffErrorMessage({
        error,
        formData: addStaffFormData,
        translate,
      }),
    };
  }
}
